<template lang="pug">
div(class="grid-container pivot-table-container")
  ejs-pivotview(
    id="pivotview"
    ref="pivotview"
    v-bind="gridProps"
  )
  save-dialog(
    :report="dataSourceSettings"
    :showDialog="showDialog"
    :title="dialogHeader"
    :rename="doRename"
    @closeDialog="closeDialog"
    @renameDialog="renameReport"
    @saveDialog="saveReport"
  )
</template>

<script lang="ts">
import { mixins } from 'vue-class-component'
import { Component, Prop, Vue, Watch } from 'vue-property-decorator'
import AlertComponent from '@/components/Alert/AlertComponent.vue'
import GridPivotTablesMixin from '@/mixins/GridPivotTablesMixin.vue'
import TotalsContainerComponent from '@/components/Totals/TotalsContainerComponent.vue'
import { DropDownListPlugin } from '@syncfusion/ej2-vue-dropdowns'
import { Icons } from '@/icons/icons'
import { Action } from 'vuex-class'
import { ModuleNamespaces } from '@/store/types/storeGlobalTypes'
import { URLS } from '@/router/routes/urlRoutes'
import LexonBreadcrumbsComponent from '@/components/forms/fields/LexonBreadcrumbs/LexonBreadcrumbsComponent.vue'
import { i18n } from '@/plugins/vue-i18n'
import { DialogTypes } from '@/store/modules/dialog/dialogTypes'
import SaveDialog from '@/views/Reports/dialogs/SaveDialog.vue'
import LfHeaderTitleComponent from '@/components/HeaderTitle/LfHeaderTitleComponent.vue'
import ReportFilterComponent from '@/views/Reports/ReportFilterComponent.vue'

const selectedRegisterModule = ModuleNamespaces.SELECTED_REGISTER
const dialogModule = ModuleNamespaces.DIALOG

Vue.use(DropDownListPlugin)

@Component({
  components: {
    AlertComponent,
    LexonBreadcrumbsComponent,
    LfHeaderTitleComponent,
    SaveDialog,
    TotalsContainerComponent,
    ReportFilterComponent
  }
})
export default class GridPivotTableComponent extends mixins(GridPivotTablesMixin) {
  @Prop({
    required: true
  })
  dataSourceSettings!: any

  @Prop({
    required: true
  })
  disableToolbarÏtems!: boolean

  @Prop({
    type: Function
  })
  fetchSettings: (id: any) => void

  @Prop({
    type: Function
  })
  saveSettings: (item: any) => void

  @Action('removeRegister', { namespace: selectedRegisterModule }) removeRegisterAction: ({}) => void
  @Action('showDialog', { namespace: dialogModule }) showDialogAction: ({}) => void

  showDialog: boolean = false

  doRename: boolean = false

  dialogHeader: any = ''

  tooltipTemplate: any =
    `<div class='pivot-tooltip-container'>` +
    `<span class="row">Fila: ` +
    '${rowHeaders}' +
    `</span>` +
    `<span class="row">Columna: ` +
    '${columnHeaders}' +
    `</span>` +
    `<span class="row">Valor: ` +
    '${value}' +
    `</span>` +
    `</div>`

  get gridProps(): object {
    return {
      ...this.bothGridsSharedProps,
      allowExcelExport: true,
      allowNumberFormatting: true,
      allowPdfExport: true,
      chartSettings: this.chartSettings,
      chartTypes: this.chartTypes,
      dataSourceSettings: this.dataSourceSettings.config,
      displayOption: this.displayOption,
      fetchReport: this.fetchReport,
      height: '100%',
      locale: this.locale,
      newReport: this.newReport,
      removeReport: this.removeReport,
      renameReport: this.renameReport,
      showFieldList: true,
      showToolbar: true,
      spinnerTemplate: this.spinnerTemplate,
      toolbar: this.toolbarOptions,
      toolbarRender: this.beforeToolbarRender,
      tooltipTemplate: this.tooltipTemplate
    }
  }

  @Watch('dataSourceSettings')
  async onDatachange() {
    await this.changeToolbarProps()
  }

  @Watch('itemsList')
  async onListChange() {
    await this.changeToolbarProps()
  }

  mounted() {
    this.addSaveButtonListener()
  }

  addSaveButtonListener() {
    const saveButton = this.$el.querySelectorAll('.e-toolbar-right .e-toolbar-item')[0]
    saveButton?.addEventListener('click', this.saveReport)
  }

  reload() {
    this.$emit('reload-pivot-table')
  }

  exportListTemplate() {
    const exportPdf = this.exportToPDF
    const exportExcel = this.exportToExcel
    const exportCSV = this.exportToCSV
    return {
      template: Vue.component('exportListTemplate', {
        template: `<div class="export-menu e-menu-wrapper e-lib e-keyboard" @mouseenter="showOptions(true)" @mouseleave="showOptions(false)">
            <ul id="pivotviewexport_menu" class="e-control e-menu e-lib e-menu-parent" role="menubar" tabindex="-1">
              <li @click="showOptions(true)" class="e-menu-item e-menu-caret-icon" :class="show ? 'e-selected' : ''" role="menuitem" aria-label="" aria-haspopup="true" aria-expanded="true">
                <span class="e-menu-icon lf-icon-send e-icons">
                </span>
                <span class="e-icons e-caret">
                </span>
              </li>
            </ul>
            <div v-if="show" class=" e-menu-popup e-lib e-popup e-control e-keyboard e-popup-open" id="export-options" style="left: 574px; top: 50px; z-index: 1000002;">
              <ul class="e-menu-parent e-ul " role="menu" tabindex="0">
                <li v-for="item in items" :key="item.id" @click="onClick(item)" class="e-menu-item" id="pivotviewgrandtotal" role="menuitem" data-value="null" tabindex="-1" aria-label="Show grand totals">
                  <span class="e-menu-icon e-icons" :class="item.icon">
                  </span>
                  <span class="e-label">
                    {{item.label}}
                  </span>
                </li>
              </ul>
            </div>
          </div>`,
        props: {},
        data() {
          return {
            show: false,
            items: [
              { icon: Icons.PDF, label: 'PDF', id: '1' },
              { icon: Icons.EXCEL, label: 'Excel', id: '2' },
              { icon: Icons.CSV, label: 'CSV', id: '3' }
            ],
            fields: { text: 'Type', iconCss: 'Class', value: 'Id' }
          }
        },
        methods: {
          onClick(e: any) {
            switch (e.label) {
              case 'PDF':
                return exportPdf()
              case 'Excel':
                return exportExcel()
              case 'CSV':
                return exportCSV()
            }
          },
          showOptions(e: boolean) {
            this.show = e
          }
        }
      })
    }
  }

  disableToolbarItems(items: object[]) {
    items.forEach((item: any) => {
      item.disabled = true
    })
  }

  changeToolbarProps() {
    let obj: any = document.getElementById('pivotview')

    if (obj) {
      obj = obj.ej2_instances[0]
      const toolbarModule = obj.toolbarModule
      const toolbarItems = toolbarModule.toolbar.items

      toolbarModule.currentReport = this.dataSourceSettings.name

      if (this.disableToolbarÏtems) {
        this.disableToolbarItems(toolbarItems)
        return
      }

      if (this.dataSourceSettings.idType === '1' || this.dataSourceSettings.idType === 1) {
        toolbarItems[9].disabled = false
        toolbarItems[6].disabled = false
        toolbarItems[8].disabled = false
      } else {
        toolbarItems[9].disabled = true
        toolbarItems[6].disabled = true
        toolbarItems[8].disabled = true
      }
      delete toolbarItems[11]
    }
  }

  openSaveDialog() {
    this.dialogHeader = this.$t('components.dialog.save_report')
    this.doRename = false
    this.showDialog = true
  }

  openRenameDialog() {
    this.dialogHeader = this.$t('components.dialog.rename_report')
    this.doRename = true
    this.showDialog = true
  }

  closeDialog() {
    this.showDialog = false
  }

  async saveReport(args: any) {
    if (!args.saveAs) {
      args.name = this.dataSourceSettings.name
      args.reportName = args.name
    }
    let item: any = {}
    let obj: any = document.getElementById('pivotview')
    if (obj) {
      obj = JSON.parse(obj.ej2_instances[0].getPersistData()).dataSourceSettings
      delete obj.dataSource
      item = { ...this.dataSourceSettings, config: { ...obj }, ...args }
    } else {
      item = { ...this.dataSourceSettings, ...args }
    }
    if (item.report) {
      item.report = JSON.parse(item.report)
      item.config = {
        ...item.report.dataSourceSettings
      }
    }
    if (item.idType === '0' || item.idType === 0) {
      item.idType = 1
      item.idReportOrigin = item.id
      delete item.id
    } else {
      item.idType = 1
      if (args.saveAs) {
        item.idReportOrigin = item.id
        delete item.id
      }
    }
    item.config.dataSource = []
    delete item.report
    delete item.saveAs
    this.closeDialog()
    await this.saveSettings(item)
    await this.changeToolbarProps()
  }

  async fetchReport(args: any) {
    let reportCollection: any[] = []
    const reportList: any = []
    if (this.itemsList) {
      reportCollection = this.itemsList
    }
    reportCollection.map((item: any) => {
      reportList.push(item.name)
    })
    args.reportName = reportList
    await this.changeToolbarProps()
  }

  openRemoveReport() {
    this.showDialogAction({
      type: DialogTypes.INFO,
      message: this.$t('components.dialog.remove_register_text', {
        register: this.dataSourceSettings.name,
        text: this.$t('components.dialog.reports_literal')
      }),
      action: this.removeReport,
      mainButtonText: this.$t('action_buttons.remove'),
      secondaryButtonText: this.$t('action_buttons.cancel')
    })
  }

  removeReport() {
    this.removeRegisterAction({
      parentCategory: this.menuParentCategoryName,
      idSelectedRegister: this.dataSourceSettings.id,
      goToURL: URLS.REPORTS,
      name: this.dataSourceSettings.name,
      dialogText: i18n.t('components.dialog.reports_literal')
    })
  }

  async renameReport(args: any) {
    const report = {
      ...this.dataSourceSettings,
      name: args.name,
      reportName: args.name,
      description: args.description,
      sourceData: []
    }
    report.config.dataSource = []
    this.closeDialog()
    await this.saveSettings(report)
    await this.fetchSettings(report.id)
    await this.changeToolbarProps()
    this.reload()
  }

  newReport() {
    const element: any = document.getElementById('pivotview')
    if (element !== null) {
      const pivotGridObj = element.ej2_instances[0]
      pivotGridObj.setProperties(
        {
          dataSourceSettings: {
            columns: [],
            rows: [],
            values: [],
            filters: []
          }
        },
        false
      )
    }
  }

  beforeToolbarRender(args: any) {
    args.customToolbar.splice(2, 0, {
      template: this.exportListTemplate,
      align: 'left'
    })

    args.customToolbar[0].prefixIcon = Icons.TABLE_DISPLAY
    args.customToolbar[1].prefixIcon = Icons.ANALYSIS
    args.customToolbar[1].suffixIcon = Icons.ANALYSIS
    args.customToolbar[3].prefixIcon = Icons.TABLE_DISPLAY_1
    args.customToolbar[3].suffixIcon = Icons.TABLE_DISPLAY_1
    args.customToolbar[4].prefixIcon = Icons.TABLE_DISPLAY_2
    args.customToolbar[4].suffixIcon = Icons.TABLE_DISPLAY_2
    args.customToolbar[5].align = 'Center'
    args.customToolbar[5].visible = false
    args.customToolbar[6].align = 'Right'
    args.customToolbar[6].prefixIcon = Icons.SAVE
    args.customToolbar[7].align = 'Right'
    args.customToolbar[7].prefixIcon = Icons.SAVE_AS
    args.customToolbar[7].click = this.openSaveDialog.bind(this)
    args.customToolbar[8].align = 'Right'
    args.customToolbar[8].prefixIcon = Icons.RENAME
    args.customToolbar[8].click = this.openRenameDialog.bind(this)
    args.customToolbar[9].align = 'Right'
    args.customToolbar[9].prefixIcon = Icons.REMOVE
    args.customToolbar[9].click = this.openRemoveReport.bind(this)
    args.customToolbar[10].prefixIcon = Icons.CONFIGURATION
    args.customToolbar[10].align = 'Right'

    if (this.dataSourceSettings.idType === '1' || this.dataSourceSettings.idType === 1) {
      args.customToolbar[9].disabled = false
      args.customToolbar[6].disabled = false
      args.customToolbar[8].disabled = false
    } else {
      args.customToolbar[9].disabled = true
      args.customToolbar[6].disabled = true
      args.customToolbar[8].disabled = true
    }
  }
}
</script>

<style lang="scss">
.grid-container {
  height: 100%;
  position: relative;

  #pivotview {
    height: 100%;
    margin-top: $spacing-xl;

    #pivotview_grid {
      @include scroll-styles;

      .e-pivotview {
        .e-valuescontent {
          opacity: 1 !important;
        }
      }

      .e-gridheader {
        color: $white-01;
        .e-movableheader {
          .e-columnheader {
            &:first-child {
              th {
                background-color: $blue-03 !important;
                div,
                span {
                  color: $white-01 !important;
                  font-weight: bold;
                }
              }
            }

            th {
              background-color: $blue-07;

              &.e-stackedheadercell {
                background-color: $blue-06;
              }
              div,
              span {
                color: $blue-01;
                font-weight: bold;
                font-size: 1em;
              }

              &.e-gtot {
                background-color: $blue-01;
                div,
                span {
                  color: $white-01;
                  text-transform: uppercase;
                }
              }
            }
          }
        }
      }

      .e-gridcontent {
        height: calc(100% - 100px);
        .e-content {
          height: 100% !important;
          display: block !important;
          table {
            /*height: 100%;*/

            tr {
              .e-gtot {
                min-height: 55px;
                background-color: $blue-01;
                &:hover {
                  td {
                    background-color: $blue-01;
                    span {
                      color: $white-01;
                    }
                  }
                }

                span {
                  color: $white-01;
                  font-weight: bold;
                  text-transform: uppercase;
                }
              }
            }

            .e-rowcell.e-unfreeze:not(.e-summary) {
              text-align: left;

              .e-cellvalue {
                padding-left: 14px;
              }
            }
          }

          .e-frozencontent {
            td {
              display: flex;
              justify-content: flex-start;
              align-items: center;
              flex-direction: row;
              min-height: 30px;
              background-color: $blue-03;

              &:hover {
                td {
                  background-color: $blue-05;
                  span {
                    color: $white-01;
                  }
                }

                .e-icons {
                  &:before,
                  &:after {
                    color: $blue-01;
                  }
                }
              }

              .e-cellvalue {
                color: $white-01;
                height: 20px;
                overflow: hidden;
                text-overflow: ellipsis;
                padding-top: 10px;
              }

              .e-icons {
                width: auto !important;

                &:before,
                &:after {
                  color: $white-01;
                }
              }
            }
          }

          .e-movablecontent {
            table {
              tr {
                td {
                  &.e-gtot {
                    background-color: $blue-01;
                    opacity: 1 !important;
                    div,
                    span {
                      color: $white-01;
                    }
                  }
                }

                &.e-frozenhover {
                  .e-gtot {
                    opacity: 0.88 !important;
                    span {
                      color: $white-01 !important;
                    }
                  }
                }
              }
            }
          }
        }
      }
    }

    .e-pivot-toolbar {
      min-height: 65px;
      overflow: visible;
      border: none;

      &-menu,
      &-menu.e-menu-wrapper ul.e-menu {
        background-color: transparent;
        border: none;
      }

      .e-toolbar-items {
        min-height: 64px;
        background: $main-050 !important;

        .e-toolbar-item {
          .e-btn {
            height: 35px;
            width: 40px !important;

            &:hover {
              border-color: rgba(0, 25, 120, 0.2) !important;
            }

            .e-icons {
              font-size: 1.2em;
              color: $blue-01;
            }
          }

          .e-menu-icon {
            font-size: 1.5em;
            color: $blue-01;
          }

          .e-caret {
            color: $blue-01;
          }
        }

        .e-toolbar-left {
          .e-btn {
            &:hover {
              background: $white-01 !important;
            }
          }
          .e-menu-item {
            &.e-focused,
            &.e-selected {
              background: $white-01 !important;
            }
          }

          .e-toolbar-item {
            .e-tbar-btn {
              background: $main-050;
            }
            .e-menu-wrapper {
              margin-top: -5px;
            }
          }
        }

        .e-toolbar-right {
          border-radius: 50px;
          right: 10px;
          height: 44px;
          margin: 10px 0px;
          padding-left: 3px;
          .e-toolbar-item {
            &.e-overlay {
              background: transparent !important;
            }

            .e-btn {
              border-radius: 50px;
              background-color: $blue-01;
              border: 3px solid $gray-03;
              height: 40px;
              width: 40px !important;

              &:hover {
                border-color: $blue-05 !important;
                .e-icons {
                  color: $blue-05;
                }
              }

              .e-icons {
                color: $white-01;
                margin-top: -4.5px;
              }
            }

            &:first-child {
              padding-left: 0;
            }

            &:last-child {
              padding-right: 0;
            }
          }
        }

        .e-toolbar-center {
          min-height: 35px;
          padding-top: 12px;
          @media (min-width: 1800px) {
            margin-left: 58% !important;
          }

          .e-control-wrapper {
            min-width: 360px !important;
            padding: 5px 10px;
            border: 0.5px solid $gray-03 !important;
          }
        }
      }
    }

    .e-pivotchart {
      background-color: $white-01;
    }
  }

  .e-pivotfieldlist-container {
    .e-title-content {
      color: $blue-01;
      font-size: 1.2em;
    }

    .e-value-field-settings {
      min-width: 350px;

      .e-dlg-header {
        color: $blue-01;
        font-size: 1.4em;
      }

      .e-dlg-closeicon-btn {
        span {
          color: $blue-01;
        }
      }

      .e-dlg-content {
        @include scroll-styles();

        .e-field-name-text-wrapper,
        .e-type-option-text,
        .e-base-field-option-text,
        .e-base-item-option-text {
          width: 100%;
          color: $blue-01;
          font-weight: 600;
          font-size: 1.2em;
        }

        .e-type-option-wrapper,
        .e-base-field-option-wrapper,
        .e-base-item-option-wrapper {
          padding-top: 8px !important;
        }

        input {
          width: calc(100% - 20px) !important;
          border: 1px solid $gray-02;
          padding: 9px 10px;
          float: left;
          margin-top: 4px;

          &:focus {
            border-color: $blue-01 !important;
          }
        }

        .e-input-group {
          border: none;
        }

        .e-input-group-icon {
          position: absolute;
          right: 10px;
          top: 50%;
          transform: translateY(-50%);
        }

        .e-control-wrapper {
          &:before,
          &:after {
            display: none;
          }
        }
      }
    }

    .e-field-header,
    .e-field-header-container,
    .e-axis-container {
      background-color: $blue-04 !important;
      color: $white-01 !important;
      .e-icons {
        color: $white-01 !important;
        &.e-selected {
          &:before {
            color: $blue-01 !important;
          }
        }
      }
    }

    .e-icons {
      &.e-drag,
      &.e-sort,
      &.e-pv-filter,
      &.e-dropdown-icon,
      &.e-remove {
        color: $blue-01 !important;
        &:hover {
          opacity: 1;
        }
      }
    }

    .e-field-list-tree-outer-div {
      @include scroll-styles();
    }

    .e-frame.e-check {
      background-color: $blue-01 !important;
      color: $white-01 !important;
    }

    .e-layout-footer .e-cancel-btn {
      max-width: 100%;
    }

    .e-footer-content {
      border-top: none !important;
      .e-btn {
        color: $blue-01 !important;
        border: 2px solid $blue-01 !important;
        background-color: $white-01;
        width: 95px;
        height: 40px;
        font-weight: 400;
        float: right;

        &:hover {
          color: $white-01 !important;
          border: 2px solid $blue-01 !important;
          background-color: $blue-01;
        }
      }
    }

    .e-member-editor-dialog {
      .e-dlg-header-content {
        .e-dlg-header {
          color: $blue-01;
          font-size: 22px;
        }

        .e-dlg-closeicon-btn {
          display: none;
        }
      }

      .e-dlg-content {
        .e-tab {
          .e-tab-header {
            .e-indicator {
              background: $blue-01 !important;
            }

            .e-tab-text {
              color: $blue-01 !important;
            }
          }
        }

        .e-selected-tab {
          .e-control-wrapper {
            border: 1px solid $gray-02;
            padding: 5px 10px;
            input {
              border: none !important;
            }
          }
        }
        .e-editor-search-container {
          .e-control-wrapper {
            border: 1px solid $blue-01;
            padding: 0 0 0 10px;
            input {
              border: none !important;
            }
          }

          .e-filter-sort {
            border: 1px solid $blue-01;
            .e-member-sort {
              color: $blue-01;
              &.e-active,
              &:active,
              &:focus,
              &:hover {
                box-shadow: none !important;
                color: $white-01;
                background-color: $blue-01;
              }
            }
          }
        }

        .e-filter-div-content {
          @include scroll-styles();

          .e-filter-option-wrapper-1,
          .e-filter-option-wrapper-2 {
            > div {
              .e-input-group {
                width: calc(100% - 20px) !important;
                border: 1px solid $gray-02 !important;
                padding: 5px 10px;
              }
            }
          }
        }

        .e-list-parent,
        .e-member-editor-container-outer-div {
          @include scroll-styles();
        }
      }

      .e-footer-content {
        .e-clear-filter-button {
          display: none;
        }

        .e-btn {
          color: $gray-02 !important;
          border: 2px solid $gray-02 !important;
          background-color: $white-01;
          width: 95px;
          height: 40px;
          font-weight: 400;
          float: right;

          &:hover,
          &:active,
          &:focus {
            color: $blue-01 !important;
            border: 2px solid $blue-01 !important;
            background-color: $white-01;
          }
        }
      }

      .e-toolbar .e-toolbar-items {
        margin-left: 0px;
      }
    }
  }
}

.e-dlg-overlay {
  background-color: $white-01;
}

.e-reportList-drop {
  border: 1px solid $blue-01;
  padding: 4px !important;
  @include scroll-styles();
}

.e-menu-popup {
  .e-menu-parent {
    &.e-ul {
      border: 1px solid $blue-01;
      padding: 4px !important;
      min-width: 150px;
      font-family: $corporate-font-bold;

      .e-menu-item {
        color: $blue-01;
        font-weight: 500;
        font-size: 14px;

        .e-label,
        .e-menu-icon {
          color: $blue-01;
          font-weight: 500;
          font-size: 14px;
        }

        &.e-separator {
          border-bottom-color: $blue-01;
        }

        &:hover {
          background-color: $blue-01;
          color: $white-01;
          border-bottom: 1px solid $white-01;
          box-shadow: 0 1px 0 $blue-01;
          padding-bottom: 1px;

          .e-label,
          .e-menu-icon {
            color: $white-01 !important;
          }

          .e-checkbox-wrapper {
            .e-frame {
              border-color: $white-01;
            }
          }
        }

        .e-checkbox-wrapper {
          .e-frame {
            border-color: $blue-01;

            &.e-check {
              background-color: $blue-01;
            }
          }
        }
      }
    }
  }
}

.pivot-tooltip-container {
  display: flex;
  flex-direction: column;
  border-radius: $cornerRadius-sm;
  opacity: 1;
  -webkit-filter: drop-shadow(0px 3px 4px rgba(113, 113, 113, 0.4));
  filter: drop-shadow(0px 3px 4px rgba(113, 113, 113, 0.4));
  -ms-filter: "progid:DXImageTransform.Microsoft.Dropshadow(OffX=0, OffY=2, Color='#444')";
  filter: "progid:DXImageTransform.Microsoft.Dropshadow(OffX=0, OffY=2, Color='#444')";
  padding: 0 8px;
  background-color: $main-050 !important;

  .row {
    @include milano-regular-14;
    color: $neutral-grey-800;
    background-color: $main-050;
    padding: 2px 6px;
  }
}

.e-pivotview div.e-cellvalue {
  overflow: hidden;
  text-overflow: ellipsis;
  margin-top: 5px;
}

.e-pivotview-report-dialog,
.e-pivot-error-dialog {
  min-height: 300px;
  border: 2px solid $blue-01;

  .e-dlg-header-content {
    .e-btn {
      span {
        color: $blue-01;
      }
    }

    .e-dlg-header {
      color: $blue-01;
      font-size: 1.9em;
      font-weight: 500;
    }
  }

  .e-dlg-content {
    .e-report-outer {
      .e-pivotview-report-label {
        width: 100%;
        color: $blue-01;
        margin-left: 10px;
        font-weight: 600;
        font-size: 1.05em;
      }

      .e-pivotview-report-input {
        width: calc(100% - 20px) !important;
        border: 1px solid $gray-02;
        padding: 9px 10px;
        float: left;
        margin-top: 4px;

        &:focus {
          border-color: $blue-01 !important;
        }
      }
    }
  }

  .e-footer-content {
    border-top: none !important;
    .e-btn {
      color: $gray-02 !important;
      border: 2px solid $gray-02 !important;
      background-color: $white-01;
      width: 95px;
      height: 40px;
      font-weight: 400;
      float: right;

      &:hover {
        color: $blue-01 !important;
        border: 2px solid $blue-01 !important;
        background-color: $white-01;
      }
    }
  }
}

.e-contextmenu-wrapper {
  #pivotview_PivotFieldListvalueFieldStringContextMenu {
    @include scroll-styles();
    .e-menu-item {
      padding: 0 10px !important;
    }
  }
  .e-menu-item {
    padding: 0 10px !important;
  }
}

.export-menu {
  position: relative;
  #export-options {
    background-color: $white-01;
    left: 0 !important;
    top: 40px !important;
  }
}

#pivotviewexport_menu {
  border-left: 1px solid $blue-01;
  height: 46px;
  margin-top: 6px;

  .e-label {
    margin-left: 3px;
  }

  li {
    &:nth-child(2) {
      display: none;
    }
  }
}
</style>
